import { useCallback, useEffect, useState } from 'react';
import { Images, CreditCard, RotateCcw, Send } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useConfirm } from './ConfirmDialog';
import { stillsLine, showsStills, chargeable } from '@/lib/frameStills';

// FRAME STILLS (backend app/frame_stills.py): one still from every clip of the approved film, $49. The team's panel in a
// film's drawer: whether the client asked for them, and where the order and its stills stand. Charge (admin and
// billing) reads her saved card from Stripe only when pressed, and says which card before it charges; Retry (a failed
// job) is admin and staff's.
export function FrameStillsPanel({ project, user }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();
  const load = useCallback(() => {
    api.get(`/projects/${project.id}/frame-stills`).then(r => setState(r.data)).catch(() => setState(null));
  }, [project.id]);
  useEffect(() => { load(); }, [load]);
  if (!showsStills(state)) return null;

  const canCharge = user.role === 'admin' || user.role === 'billing';
  const canRetry = user.role === 'admin' || user.role === 'staff';
  const { order, job, price } = state;
  const line = stillsLine(state);

  const act = async (path, done) => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/frame-stills/${path}`);
      setState(r.data.state || r.data);
      done && done(r.data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'That did not go through. Try again.');
    } finally { setBusy(false); }
  };
  const charge = async () => {
    // Her saved card is asked of Stripe only now, when someone means to charge it, never on every drawer open
    let card = null;
    try { card = (await api.get(`/projects/${project.id}/frame-stills?card=1`)).data.card; }
    catch { toast.error('Could not read her saved card. Try again in a minute.'); return; }
    const what = card ? `Charge $${price} to ${project.client_name || 'the client'}'s ${card.brand} ending ${card.last4}?`
      : `${project.client_name || 'The client'} has no saved card. Email her a link to pay $${price}?`;
    const ok = await confirm({ title: 'Frame Stills', message: `${what} One still from every clip of the approved film, delivered beside it.`,
      confirmLabel: card ? `Charge $${price}` : 'Email the link' });
    if (!ok) return;
    act('charge', (d) => d.result === 'charged'
      ? toast.success(`Charged $${price} to the ${d.card.brand} ending ${d.card.last4}`)
      : toast.success(`Payment link emailed (${d.reason})`));
  };

  return (
    <div data-testid="frame-stills-panel" data-state={job?.state || (order ? order.payment_status : 'none')}
      className="mt-2 px-3 py-2 rounded-md bg-white/[0.02] border border-ug-border flex items-start gap-2">
      <Images className="w-3.5 h-3.5 text-ug-gold/70 shrink-0 mt-0.5" />
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/80">Frame Stills</span>
        </div>
        <span data-testid="frame-stills-line" className="font-sans text-[10px] text-slate-400">{line}</span>
        {job?.state === 'failed' && job.error && (
          <span data-testid="frame-stills-error" className="font-sans text-[10px] text-ug-status-red/90">{job.error}</span>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {canCharge && chargeable(state) && (
            <button onClick={charge} disabled={busy} data-testid="frame-stills-charge-btn"
              className="tap-auto h-6 flex items-center gap-1 font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/80 hover:text-ug-gold border border-ug-gold/25 hover:border-ug-gold/50 px-2 rounded transition-colors disabled:opacity-40">
              <CreditCard className="w-3 h-3" /> Charge ${price}
            </button>
          )}
          {canCharge && order && order.payment_status !== 'paid' && (
            <>
              <button onClick={charge} disabled={busy} data-testid="frame-stills-charge-again-btn"
                className="tap-auto h-6 flex items-center gap-1 font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/80 hover:text-ug-gold border border-ug-gold/25 hover:border-ug-gold/50 px-2 rounded transition-colors disabled:opacity-40">
                <CreditCard className="w-3 h-3" /> Charge again
              </button>
              <button onClick={() => act('resend-link', () => toast.success('Payment link emailed again'))} disabled={busy} data-testid="frame-stills-resend-btn"
                className="tap-auto h-6 flex items-center gap-1 font-sans text-[9px] font-semibold uppercase tracking-wider text-slate-400 hover:text-ug-gold border border-ug-border hover:border-gold-leaf/30 px-2 rounded transition-colors disabled:opacity-40">
                <Send className="w-3 h-3" /> Send the link again
              </button>
            </>
          )}
          {canRetry && job?.state === 'failed' && (
            <button onClick={() => act('retry', () => toast.success('Frame Stills queued again'))} disabled={busy} data-testid="frame-stills-retry-btn"
              className="tap-auto h-6 flex items-center gap-1 font-sans text-[9px] font-semibold uppercase tracking-wider text-ug-gold/80 hover:text-ug-gold border border-ug-gold/25 hover:border-ug-gold/50 px-2 rounded transition-colors disabled:opacity-40">
              <RotateCcw className="w-3 h-3" /> Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
